import { computed, readonly, ref, shallowRef, watch } from 'vue';
import { useAuthStore } from '@/entities/Session';
import { useUserStore } from '@/entities/User';
import { getFolderTree } from '../api/folder-tree.api';
import {
  buildFolderCodeIndex,
  buildFolderIndex,
  buildModuleCodeIndex,
} from './mapVisibleTreeToNavigation';
import type {
  VisibleDirectoryTree,
  VisibleFolderEntry,
  VisibleModuleNode,
} from './visible-tree.types';

interface PendingLoad {
  key: string;
  promise: Promise<VisibleDirectoryTree>;
}

const createEmptyTree = (): VisibleDirectoryTree => ({ modules: [], fixedFolders: [] });

const tree = shallowRef<VisibleDirectoryTree>(createEmptyTree());
const loadedKey = ref<string | null>(null);
// The key the tree on screen belongs to. It outlives `loadedKey` while the tree is revalidated, so a
// load for another user or branch still clears it instead of showing the old branch's modules.
let treeKey: string | null = null;
const loading = ref(false);
const hasError = ref(false);
let pending: PendingLoad | null = null;

const folderIndex = computed(() => buildFolderIndex(tree.value));
const codeIndex = computed(() => buildFolderCodeIndex(tree.value, folderIndex.value));
const moduleCodeIndex = computed(() => buildModuleCodeIndex(tree.value));
const visibleTree = computed(() => tree.value);

function resolveCacheKey(): string {
  const userStore = useUserStore();
  const authStore = useAuthStore();
  return `${userStore.id ?? ''}:${authStore.selectedBranchId ?? ''}`;
}

export function loadVisibleDirectoryTree(): Promise<VisibleDirectoryTree> {
  if (!useAuthStore().isAuthenticated) {
    resetVisibleDirectoryTreeCache();
    return Promise.resolve(tree.value);
  }

  const key = resolveCacheKey();

  if (loadedKey.value === key) return Promise.resolve(tree.value);
  if (pending?.key === key) return pending.promise;

  loadedKey.value = null;
  if (treeKey !== key) {
    tree.value = createEmptyTree();
    treeKey = null;
  }

  loading.value = true;
  hasError.value = false;

  const promise: Promise<VisibleDirectoryTree> = getFolderTree()
    .then((loadedTree) => {
      if (pending?.promise === promise) {
        tree.value = loadedTree;
        loadedKey.value = key;
        treeKey = key;
      }
      return loadedTree;
    })
    .catch((error: unknown) => {
      if (pending?.promise === promise) {
        hasError.value = true;
      }
      throw error;
    })
    .finally(() => {
      if (pending?.promise === promise) {
        pending = null;
        loading.value = false;
      }
    });

  pending = { key, promise };
  return promise;
}

export function findVisibleFolderByCode(code: string): VisibleFolderEntry | null {
  return codeIndex.value.get(code) ?? null;
}

export function findVisibleModuleByCode(code: string): VisibleModuleNode | null {
  return moduleCodeIndex.value.get(code) ?? null;
}

export function findVisibleFolderById(folderId: number): VisibleFolderEntry | null {
  return folderIndex.value.get(folderId) ?? null;
}

/**
 * The next load fetches again while the current tree stays on screen. Dropping `pending` makes a
 * response already in flight, read before the change, lose the `pending?.promise === promise` check.
 */
export function markVisibleDirectoryTreeStale(): void {
  loadedKey.value = null;
  pending = null;
  loading.value = false;
}

export function resetVisibleDirectoryTreeCache(): void {
  tree.value = createEmptyTree();
  treeKey = null;
  loadedKey.value = null;
  loading.value = false;
  hasError.value = false;
  pending = null;
}

interface UseVisibleDirectoryTreeOptions {
  autoLoad?: boolean;
}

export function useVisibleDirectoryTree(options: UseVisibleDirectoryTreeOptions = {}) {
  if (options.autoLoad) {
    const cacheKey = computed(resolveCacheKey);
    watch(
      cacheKey,
      () => {
        loadVisibleDirectoryTree().catch(() => undefined);
      },
      { immediate: true }
    );
  }

  return {
    tree: visibleTree,
    loading: readonly(loading),
    hasError: readonly(hasError),
    folderIndex,
    codeIndex,
    load: loadVisibleDirectoryTree,
    findFolderByCode: findVisibleFolderByCode,
    findFolderById: findVisibleFolderById,
    findModuleByCode: findVisibleModuleByCode,
  };
}
